'use client';

import { useEffect } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { Button, Text } from '@holistic/components';
import {
  AlertCircle,
  ArrowLeft,
  House,
  RefreshCw,
} from '@holistic/components/icons';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const router = useRouter();

  useEffect(() => {
    console.error('Unhandled Application Error:', error);
  }, [error]);

  const errorDetails = error.stack || error.message || null;

  return (
    <div className="bg-background relative flex min-h-screen flex-col items-center justify-center overflow-hidden p-6 text-center">
      {/* Decorative background glow */}
      <div className="bg-destructive/10 pointer-events-none absolute -top-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full blur-3xl" />
      <div className="bg-primary/5 pointer-events-none absolute right-0 bottom-0 h-96 w-96 translate-x-1/3 translate-y-1/3 rounded-full blur-3xl" />

      <div className="relative flex w-full max-w-lg flex-col items-center gap-6">
        <Link href="/" className="shrink-0">
          <Image
            src="/yeniden-siyah-logo.svg"
            alt="Yeniden.co"
            width={1011}
            height={214}
            className="h-7 w-auto"
            priority
          />
        </Link>

        {/* Big error status code & icon */}
        <div className="relative flex items-center justify-center">
          <Text
            element="h1"
            className="from-destructive to-destructive/40 bg-linear-to-b bg-clip-text text-[7rem] leading-none font-black tracking-tighter text-transparent select-none sm:text-[10rem]"
          >
            500
          </Text>
          <span className="bg-background border-destructive/20 absolute -top-2 -right-2 flex h-14 w-14 animate-pulse items-center justify-center rounded-full border shadow-lg sm:h-16 sm:w-16">
            <AlertCircle className="text-destructive h-7 w-7 sm:h-8 sm:w-8" />
          </span>
        </div>

        <div className="flex flex-col items-center gap-2">
          <Text element="h2" className="text-2xl font-semibold">
            Bir Hata Oluştu
          </Text>
          <Text
            element="p"
            className="text-muted-foreground max-w-md text-sm leading-relaxed"
          >
            Beklenmeyen bir hata meydana geldi. Sayfayı yenileyerek veya geri
            dönerek tekrar deneyebilirsiniz.
          </Text>
        </div>

        {errorDetails && (
          <details className="bg-muted/40 border-border text-muted-foreground max-h-48 w-full overflow-auto rounded-lg border p-3 text-left font-mono text-xs">
            <summary className="text-foreground mb-1 cursor-pointer font-medium select-none">
              Hata Detayları
            </summary>
            <pre className="bg-background/50 border-border/50 mt-2 rounded border p-2 break-words whitespace-pre-wrap">
              {errorDetails}
            </pre>
          </details>
        )}

        <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
          <Button
            variant="outline"
            className="cursor-pointer gap-2"
            onClick={() => reset()}
          >
            <RefreshCw className="h-4 w-4" />
            Yenile
          </Button>
          <Button
            variant="outline"
            className="cursor-pointer gap-2"
            onClick={() => router.back()}
          >
            <ArrowLeft className="h-4 w-4" />
            Geri Dön
          </Button>
          <Button
            className="cursor-pointer gap-2"
            onClick={() => (window.location.href = '/')}
          >
            <House className="h-4 w-4" />
            Ana Sayfaya Dön
          </Button>
        </div>
      </div>
    </div>
  );
}
